import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api, { type Quote } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import UnifiedTimeline from '../components/UnifiedTimeline'

const STATUSES: Quote['status'][] = [
  'NEW',
  'PRICING',
  'QUOTED',
  'SENT_TO_CUSTOMER',
  'ACCEPTED',
  'CONVERTED_TO_DRAFT_LOAD',
  'EXPIRED',
  'REJECTED',
]

export default function QuoteDetailPage() {
  const navigate = useNavigate()
  const { hasRole } = useAuth()
  const isDriver = hasRole('DRIVER')
  const canDelete = hasRole('SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER', 'BROKER_ADMIN')
  const { quoteId } = useParams<{ quoteId: string }>()
  const [quote, setQuote] = useState<Quote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [converting, setConverting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const [status, setStatus] = useState<Quote['status']>('NEW')
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerReference, setCustomerReference] = useState('')
  const [internalReference, setInternalReference] = useState('')
  const [customerQuote, setCustomerQuote] = useState('')
  const [targetCarrierPay, setTargetCarrierPay] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [pricingNotes, setPricingNotes] = useState('')
  const [publicNotes, setPublicNotes] = useState('')

  useEffect(() => {
    if (!quoteId) return
    setLoading(true)
    api.quotes.get(quoteId)
      .then((response) => {
        const q = response.data
        setQuote(q)
        setStatus(q.status)
        setCustomerName(q.customerName ?? '')
        setCustomerPhone(q.customerPhone ?? '')
        setCustomerEmail(q.customerEmail ?? '')
        setCustomerReference(q.customerReference ?? '')
        setInternalReference(q.internalReference ?? '')
        setCustomerQuote(typeof q.customerQuoteCents === 'number' ? String(q.customerQuoteCents / 100) : '')
        setTargetCarrierPay(typeof q.targetCarrierPayCents === 'number' ? String(q.targetCarrierPayCents / 100) : '')
        setInternalNotes(q.internalNotes ?? '')
        setPricingNotes(q.pricingNotes ?? '')
        setPublicNotes(q.publicNotes ?? '')
      })
      .catch((err) => setError(err?.response?.data?.error ?? 'Failed to load quote'))
      .finally(() => setLoading(false))
  }, [quoteId])

  const summary = useMemo(() => {
    const quoteNum = customerQuote ? Number(customerQuote) : null
    const carrierNum = targetCarrierPay ? Number(targetCarrierPay) : null
    const margin = quoteNum != null && carrierNum != null ? quoteNum - carrierNum : null
    const miles = quote?.estDistanceMiles ?? null
    return {
      margin,
      customerRpm: quoteNum != null && miles ? quoteNum / miles : null,
      carrierRpm: carrierNum != null && miles ? carrierNum / miles : null,
    }
  }, [customerQuote, targetCarrierPay, quote?.estDistanceMiles])

  const timelineQuery = useQuery({
    queryKey: ['quote-detail-unified-timeline', quote?.convertedOrderId],
    queryFn: async () => {
      if (!quote?.convertedOrderId) return []
      return (await api.orders.unifiedTimeline(quote.convertedOrderId)).data.timeline
    },
    enabled: Boolean(quote?.convertedOrderId),
  })

  const saveQuote = async () => {
    if (!quoteId) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const response = await api.quotes.update(quoteId, {
        status,
        customerName,
        customerPhone,
        customerEmail,
        customerReference,
        internalReference,
        customerQuoteCents: customerQuote ? Math.round(Number(customerQuote) * 100) : undefined,
        targetCarrierPayCents: targetCarrierPay ? Math.round(Number(targetCarrierPay) * 100) : undefined,
        internalNotes,
        pricingNotes,
        publicNotes,
      })
      setQuote(response.data.quote)
      setNotice('Quote updated.')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update quote')
    } finally {
      setSaving(false)
    }
  }

  const convertToDraftLoad = async () => {
    if (!quoteId) return
    setConverting(true)
    setError(null)
    setNotice(null)
    try {
      const response = await api.quotes.convertToDraftLoad(quoteId)
      setQuote(response.data.quote)
      if (response.data.converted) {
        setNotice(`Quote converted to draft load ${response.data.loadNumber ?? response.data.loadId}.`)
      } else {
        setNotice('Quote already converted. Opening existing draft load link below.')
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to convert quote')
    } finally {
      setConverting(false)
    }
  }

  const deleteQuote = async () => {
    if (!quoteId) return
    setDeleting(true)
    setError(null)
    setNotice(null)
    try {
      await api.quotes.delete(quoteId)
      navigate('/quotes')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to delete quote')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading quote...</div>
  if (error && !quote) return <div className="p-6 text-sm text-red-600">{error}</div>
  if (!quote) return <div className="p-6 text-sm text-gray-500">Quote not found.</div>

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{quote.quoteNumber}</h1>
          <p className="text-sm text-gray-500 mt-1">Quote detail and conversion to draft load.</p>
        </div>
        <Link to="/quotes" className="text-sm text-blue-700 hover:underline">← Back to Quotes</Link>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {notice && <div className="rounded border border-green-200 bg-green-50 text-green-700 px-3 py-2 text-sm">{notice}</div>}

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-gray-500">Status</div>
          <select className="border rounded px-2 py-1 mt-1 w-full" value={status} onChange={e => setStatus(e.target.value as Quote['status'])}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div className="text-gray-500">Created</div>
          <div className="mt-1">{quote.createdAt ? new Date(quote.createdAt).toLocaleString() : '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Updated</div>
          <div className="mt-1">{quote.updatedAt ? new Date(quote.updatedAt).toLocaleString() : '—'}</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <h2 className="md:col-span-2 font-semibold">Customer</h2>
        <div className="md:col-span-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
          Customer: <span className="font-semibold text-gray-900">{customerName || 'Not provided'}</span>
        </div>
        <input className="border rounded px-3 py-2 text-sm" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Customer Name" />
        <input className="border rounded px-3 py-2 text-sm" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Phone" />
        <input className="border rounded px-3 py-2 text-sm" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="Email" />
        <input className="border rounded px-3 py-2 text-sm" value={customerReference} onChange={e => setCustomerReference(e.target.value)} placeholder="Customer Ref" />
        <input className="md:col-span-2 border rounded px-3 py-2 text-sm" value={internalReference} onChange={e => setInternalReference(e.target.value)} placeholder="Internal Ref" />
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <h2 className="md:col-span-3 font-semibold">Route & Vehicle Snapshot</h2>
        <div>Origin: {quote.originCity ?? ''} {quote.originState ?? ''} {quote.originZip ?? ''}</div>
        <div>Destination: {quote.destinationCity ?? ''} {quote.destinationState ?? ''} {quote.destinationZip ?? ''}</div>
        <div>Mileage: {quote.estDistanceMiles ?? '—'} mi ({quote.mileageProvider ?? '—'} / {quote.mileageSource ?? '—'})</div>
        <div>VIN: {quote.vin ?? '—'}</div>
        <div>Vehicle: {[quote.vehicleYear, quote.vehicleMake, quote.vehicleModel, quote.vehicleType].filter(Boolean).join(' ') || '—'}</div>
        <div>VIN Status: {quote.vinStatus ?? '—'}</div>
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <h2 className="md:col-span-3 font-semibold">Pricing</h2>
        <input className="border rounded px-3 py-2 text-sm" value={customerQuote} onChange={e => setCustomerQuote(e.target.value)} placeholder="Customer Quote ($)" />
        <input className="border rounded px-3 py-2 text-sm" value={targetCarrierPay} onChange={e => setTargetCarrierPay(e.target.value)} placeholder="Target Carrier Pay ($)" />
        <div className="text-sm text-gray-600">Margin: {summary.margin == null ? '—' : `$${summary.margin.toFixed(2)}`}</div>
        <div className="text-sm text-gray-600">Customer RPM: {summary.customerRpm == null ? '—' : `$${summary.customerRpm.toFixed(2)}/mi`}</div>
        <div className="text-sm text-gray-600">Carrier RPM: {summary.carrierRpm == null ? '—' : `$${summary.carrierRpm.toFixed(2)}/mi`}</div>
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 gap-3">
        <h2 className="font-semibold">Notes</h2>
        <textarea className="border rounded px-3 py-2 text-sm" rows={2} value={internalNotes} onChange={e => setInternalNotes(e.target.value)} placeholder="Internal notes" />
        <textarea className="border rounded px-3 py-2 text-sm" rows={2} value={pricingNotes} onChange={e => setPricingNotes(e.target.value)} placeholder="Pricing notes" />
        <textarea className="border rounded px-3 py-2 text-sm" rows={2} value={publicNotes} onChange={e => setPublicNotes(e.target.value)} placeholder="Public notes" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={saveQuote} disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save / Update Quote'}
        </button>
        <button
          onClick={convertToDraftLoad}
          disabled={converting || !!quote.convertedLoadId}
          className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {quote.convertedLoadId ? 'Already Converted' : converting ? 'Converting...' : 'Convert to Draft Load'}
        </button>
        {canDelete && !isDriver && (
          <button
            onClick={() => {
              if (!confirm('Are you sure you want to delete this quote?')) return
              void deleteQuote()
            }}
            disabled={deleting}
            className="px-4 py-2 rounded-lg border border-red-300 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
        {quote.convertedLoadId && (
          <Link to={`/loads/${quote.convertedLoadId}`} className="px-4 py-2 rounded-lg border text-sm text-blue-700 hover:underline">
            Open Converted Draft Load
          </Link>
        )}
      </div>

      {quote.convertedOrderId ? (
        <UnifiedTimeline title="Unified Timeline" entries={timelineQuery.data ?? []} defaultFilter="operations" />
      ) : (
        <div className="bg-white border rounded-xl p-4 text-sm text-gray-500">Unified timeline becomes available after the converted load enters the dispatch workflow.</div>
      )}
    </div>
  )
}
